import type { EmployeeKeyInformationDto } from "@/generated/model/employeeKeyInformationDto";
import { useProfileSectionEditing } from "@/hooks/use-profile-section-form";
import {
  employmentStatusLabel,
  formatProfileDate,
  fullName,
  NOT_RECORDED,
  textOrNotRecorded,
} from "@/lib/people";
import { nurseryRoleLabel } from "@/lib/roles";
import { staffUsername } from "@/lib/staff-username";

import { EditKeyInformationForm } from "../forms/EditKeyInformationForm";
import { ProfileSectionCard } from "../ProfileSectionCard";
import { ProfileSectionEditButton } from "../ProfileSectionEditButton";

interface KeyInformationSectionProps {
  nurseryId: string;
  userId: string;
  keyInformation: EmployeeKeyInformationDto;
  canEdit: boolean;
}

export function KeyInformationSection({
  nurseryId,
  userId,
  keyInformation,
  canEdit,
}: KeyInformationSectionProps) {
  const { editing, editButtonRef, startEditing, stopEditing } =
    useProfileSectionEditing();

  return (
    <ProfileSectionCard
      title="Key information"
      testId="profile-section-key-information"
      action={
        canEdit &&
        !editing && (
          <ProfileSectionEditButton
            ref={editButtonRef}
            section="key information"
            onClick={startEditing}
          />
        )
      }
      editForm={
        canEdit &&
        editing && (
          <EditKeyInformationForm
            nurseryId={nurseryId}
            userId={userId}
            keyInformation={keyInformation}
            onDone={stopEditing}
          />
        )
      }
      fields={[
        {
          label: "Full name",
          value: textOrNotRecorded(
            fullName(keyInformation.firstName, keyInformation.lastName),
          ),
        },
        {
          label: "Preferred name",
          value: textOrNotRecorded(keyInformation.preferredName),
        },
        {
          label: "Pronouns",
          value: textOrNotRecorded(keyInformation.pronouns),
        },
        { label: "Username", value: staffUsername(keyInformation.email) },
        {
          label: "Employee ID",
          value: textOrNotRecorded(keyInformation.employeeId),
        },
        {
          label: "Job title",
          value: textOrNotRecorded(keyInformation.jobTitle),
        },
        {
          label: "Role",
          value: nurseryRoleLabel(keyInformation.nurseryRole) || NOT_RECORDED,
        },
        {
          label: "Team / room",
          value: textOrNotRecorded(keyInformation.room?.name),
        },
        {
          label: "Line manager",
          value: textOrNotRecorded(keyInformation.lineManager?.name),
        },
        {
          label: "Start date",
          value: formatProfileDate(keyInformation.startDate),
        },
        { label: "End date", value: formatProfileDate(keyInformation.endDate) },
        {
          label: "Employment status",
          value: employmentStatusLabel(keyInformation.employmentStatus),
        },
      ]}
    />
  );
}
